import { useForm, Link, Head } from '@inertiajs/react'
import Logo from '@/components/Logo'
import GoogleOAuthButton from '@/components/GoogleOAuthButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { FormEvent, ReactNode } from 'react'

type Props = {
  prefill_email?: string | null
  checkout_session_id?: string | null
  purchase_id?: string | null
}

export default function UserSignUp({ prefill_email, checkout_session_id, purchase_id }: Props = {}) {
  const { data, setData, post, processing, errors, transform } = useForm({
    first_name: '',
    last_name: '',
    email: prefill_email ?? '',
    username: '',
    password: '',
    password_confirmation: '',
    checkout_session_id: checkout_session_id ?? '',
    purchase_id: purchase_id ?? '',
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => {
      const { checkout_session_id: sessionId, purchase_id: purchaseId, ...user } = formData
      return { user, checkout_session_id: sessionId, purchase_id: purchaseId }
    })
    post('/users/sign_up')
  }

  return (
    <>
      <Head title="Sign Up" />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-accent/10 to-primary/5 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-semibold">Create your account</CardTitle>
          <CardDescription>Join Gala Season as a philanthropist</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <GoogleOAuthButton accountType="user" label="Sign up with Google" />
          <div className="relative">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-sm text-muted-foreground">
              or
            </span>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First name</Label>
                <Input
                  id="first_name"
                  value={data.first_name}
                  onChange={e => setData('first_name', e.target.value)}
                  autoFocus
                />
                {errors.first_name && (
                  <p className="text-base text-destructive">{errors.first_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last name</Label>
                <Input
                  id="last_name"
                  value={data.last_name}
                  onChange={e => setData('last_name', e.target.value)}
                />
                {errors.last_name && (
                  <p className="text-base text-destructive">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={e => setData('email', e.target.value)}
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-base text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={data.username}
                onChange={e => setData('username', e.target.value)}
              />
              {errors.username && (
                <p className="text-base text-destructive">{errors.username}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={e => setData('password', e.target.value)}
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="text-base text-destructive">{errors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirmation">Confirm password</Label>
              <Input
                id="password_confirmation"
                type="password"
                value={data.password_confirmation}
                onChange={e => setData('password_confirmation', e.target.value)}
                autoComplete="new-password"
              />
              {errors.password_confirmation && (
                <p className="text-base text-destructive">{errors.password_confirmation}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={processing}>
              Sign Up
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-2 text-center text-base">
          <p>
            Already have an account?{' '}
            <Link href="/users/sign_in" className="underline underline-offset-4 hover:text-primary">
              Sign in
            </Link>
          </p>
          <p>
            <Link href="/organizations/sign_up" className="underline underline-offset-4 hover:text-primary">
              Are you an organization?
            </Link>
          </p>
        </CardFooter>
      </Card>
      </div>
    </div>
    </>
  )
}

UserSignUp.layout = (page: ReactNode) => page
